import { Component, DestroyRef, input, InputSignal, OnInit, signal, WritableSignal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { IChatMessage, IChatMessages, IChatReadMessage, IChatReadUser, IChatUser, IMessageLocalData, INewChatMessage } from '../../models/chat';
import { PhChatMessagesComponent } from '../ph-chat-messages/ph-chat-messages.component';
import { PhChatInputComponent } from '../ph-chat-input/ph-chat-input.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { ChatService } from '../../services/chat.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackBarService } from '../../services/snack-bar.service';
import { SessionService } from '../../services/session.service';
import * as uuid from 'uuid';
import { RequestParams } from '../../models/requestParams';
import { SocketService } from '../../services/socket.service';
import { Utils } from '../../services/utils.service';

const EMPTY_RESPONSE: IChatMessages = {
    results: [],
    count: 0,
    next: null,
    previous: null
};

@Component({
  selector: 'ph-chat',
  templateUrl: './ph-chat.component.html',
  styleUrls: ['./ph-chat.component.scss'],
  imports: [
      MatTabsModule,
      PhChatMessagesComponent,
      PhChatInputComponent,
      PhLoaderComponent,
      NgTemplateOutlet,
      TranslateModule
  ]
})
export class PhChatComponent implements OnInit {

    chatName: InputSignal<string> = input('New chat');
    emptySubtitle: InputSignal<string> = input('CHAT.WRITE_FOR_USE');

    private _requestParams: RequestParams;
    loading: WritableSignal<boolean> = signal<boolean>(true);
    messages: WritableSignal<IChatMessages> = signal<IChatMessages>(EMPTY_RESPONSE);

    constructor(
        private readonly chatService: ChatService,
        private readonly destroyRef: DestroyRef,
        private readonly snackBarService: SnackBarService,
        private readonly sessionService: SessionService,
        private readonly socketService: SocketService
    ) {
        this._requestParams = new RequestParams({ page_size: 30 });
    }

    ngOnInit(): void {
        this.loading.set(true);
        if (!Utils.isNullOrUndefined(this.chatService.focusMessageId())) {
            this.loadAroundMessage(this.chatService.focusMessageId());
        } else {
            this.loadLastReadMessages();
        }

        this.chatService.loadLastMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.loadLastMessages());

        this.chatService.newMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message: INewChatMessage): void => {
                const id: string = String(uuid.v4());
                const localData: IMessageLocalData = message.localData;
                delete message.localData;
                
                this.chatService.sendMessage(this.chatService.id, message)
                    .subscribe({
                        next: (savedMessage: IChatMessage): void => {
                            const saveReadSocket: IChatReadMessage | undefined = this.socketService.chatReadMessage.value;
                            if (saveReadSocket?.message_ids.includes(savedMessage.id as number)) {
                                const readUsersIds: Array<number> = savedMessage.read_by.map((user: IChatReadUser) => user.id);
                                if (!readUsersIds.includes(saveReadSocket.user.id)) {
                                    savedMessage.read_by.push(saveReadSocket.user);
                                }
                            }

                            this.messages.update((current: IChatMessages): IChatMessages => {
                                const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) => {
                                    return msg.id === id ? savedMessage : msg;
                                });
                                return { ...current, results };
                            });
                            if (this.messages().previous) this.chatService.scrollToBottom$.next();
                        },
                        error: (): void => {
                            const failedMessage: IChatMessage | undefined = this.messages().results.find((msg: IChatMessage) => msg.id === id);
                            if (failedMessage) failedMessage.status = 'failed';
                            if (this.messages().previous) this.chatService.scrollToBottom$.next();
                            this.snackBarService.setErrorNotification();
                        }
                    });
                
                if (this.messages().previous) return;
                const tempMessage: IChatMessage = {
                    user: {
                        id: this.sessionService.defaults.user_id,
                        email: this.sessionService.defaults.email,
                        photo: this.sessionService.defaults.image,
                        full_name: this.sessionService.defaults.fullName
                    },
                    content: message.content,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    attachments: message.attachments?.map((attachment: { key: string, name: string, file: string }) => ({
                        file: attachment.file,
                        key: attachment.key,
                        name: attachment.name,
                        content_type: attachment.name.split('.').pop() || ''
                    })) || [],
                    id: id
                };

                if (message.reply_to) {
                    tempMessage.reply_to = {
                        id: message.reply_to,
                        content: localData?.replyContent,
                        user: localData?.replyUser
                    };
                }

                this.messages.update((current: IChatMessages): IChatMessages => {
                    return {
                        ...current,
                        results: [...current.results, tempMessage]
                    };
                });
            });

        this.socketService.chatNewMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message: IChatMessage }): void => {
                if (this.messages().previous) return;
                
                this.messages.update((current: IChatMessages): IChatMessages => {
                    const isDuplicate = current.results.some((msg: IChatMessage) => {
                        if (msg.id === response.message.id) return true;
                        
                        const isTemp: boolean = typeof msg.id === 'string' && msg.id.includes('-');
                        const sameUser: boolean = msg.user?.id === response.message.user?.id;
                        const sameContent: boolean = (msg.content ?? '').trim() === (response.message.content ?? '').trim();
                        
                        return isTemp && sameUser && sameContent;
                    });

                    if (isDuplicate) return current;

                    this.chatService.checkScrollBottomPosition$.next();

                    return {
                        ...current,
                        results: [...current.results, response.message]
                    };
                });

                this.readMessages();
            });

        this.socketService.chatReadMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: IChatReadMessage): void => {
                if (!response) return;
                const readedMessages: Array<IChatMessage> = this.messages().results
                    .filter((msg: IChatMessage) => typeof msg.id === 'number' && response.message_ids.includes(msg.id));

                for (let message of readedMessages) {
                    const readUsersIds: Array<number> = message.read_by.map((user: IChatReadUser) => user.id);
                    if (!readUsersIds.includes(response.user.id)) {
                        message.read_by.push(response.user);
                    }
                }

                this.messages.update((current: IChatMessages): IChatMessages => ({
                    ...current,
                    results: [...current.results]
                }));
            });

        this.chatService.loadNextMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const nextUrl: string = this.messages().next;
                if (!nextUrl || this.chatService.isLoadingNext()) return;

                this.chatService.loadNextMessages(nextUrl)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((response: IChatMessages) => {
                        this.messages.update((current: IChatMessages) => ({
                            ...response,
                            previous: current.previous,
                            results: [...response.results, ...current.results]
                        }));
                        this.readMessages();
                    });
            });

        this.chatService.loadPreviousMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const previousUrl: string = this.messages().previous;
                if (!previousUrl || this.chatService.isLoadingPrevious()) return;

                this.chatService.loadPreviousMessages(previousUrl)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((response: IChatMessages) => {
                        this.messages.update((current: IChatMessages) => ({
                            ...response,
                            next: current.next,
                            results: [...current.results, ...response.results]
                        }));
                        this.readMessages();
                    });
            });

        this.chatService.scrollToDay$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((date: string) => {
                if (this.chatService.isLoadingNext()) return;
                this.chatService.isLoadingNext.set(true);
                this.chatService
                    .getMessagesAround(this.chatService.id, 'date', { date: date, after_limit: 30, before_limit: 30, page_size: 30 })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (response: IChatMessages) => {
                            this.messages.set({
                                ...response,
                                anchor_message_id: undefined,
                                anchor_date: date
                            });
                            this.readMessages();
                            this.chatService.isLoadingNext.set(false);
                        },
                        error: (): void => {
                            this.chatService.isLoadingNext.set(false);
                            this.snackBarService.setErrorNotification();
                        }
                });
            });

        this.chatService.loadAroundMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((messageId: number) => this.loadAroundMessage(messageId));
    }

    private loadAroundMessage(messageId: number): void {
        this.chatService.isLoadingNext.set(true);
        this.chatService.getMessagesAround(
                this.chatService.id, 
                'message', 
                { after_limit: 30, before_limit: 30, page_size: 30, message_id: messageId }
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);
                    this.loading.set(false);
                    this.readMessages();
                    this.chatService.isLoadingNext.set(false);
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                    this.chatService.isLoadingNext.set(false);
                }
            });
    }

    private loadLastMessages(): void {
        this.chatService.getMessages(this.chatService.id, this._requestParams.getParams())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);    
                    this.loading.set(false);
                    this.chatService.markAsRead(this.chatService.id).subscribe();
                    setTimeout(() => this.chatService.scrollToBottom$.next());
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                }
            });
    }

    private loadLastReadMessages(): void {
        this.chatService.getMessagesAround(this.chatService.id, 'unread', { after_limit: 30, before_limit: 30, page_size: 30 })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);
                    this.loading.set(false);
                    this.readMessages();
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                }
            });
    }

    private readMessages(): void {
        const unreadedMessagesIds: Array<number | string> = this.messages().results
            .filter((msg: IChatMessage) => msg.user.id !== this.sessionService.defaults.user_id)
            .filter((msg: IChatMessage) => {
                const readUsersIds: Array<number> = msg.read_by?.map((user: { id: number }) => user.id) || [];
                return !readUsersIds.includes(this.sessionService.defaults.user_id);
            })
            .map((msg: IChatMessage) => msg.id);

            if (!unreadedMessagesIds.length) return;
            this.chatService.markAsRead(this.chatService.id, { ids: unreadedMessagesIds.join(',') })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
    }
}
